import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeftRight,
  Download,
  Upload,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  ShoppingCart,
  Package,
  Truck,
  CreditCard,
  User,
  MapPin,
  Calendar,
  Eye,
  Send,
  Filter,
  Search,
  MoreHorizontal
} from "lucide-react";

const API = "/api";

const ORDER_STATUSES = {
  "unfulfilled": { label: "Kargolanmadı", color: "bg-amber-500" },
  "fulfilled": { label: "Kargolandı", color: "bg-emerald-500" },
  "partially_fulfilled": { label: "Kısmen Kargolandı", color: "bg-blue-500" },
  "cancelled": { label: "İptal", color: "bg-red-500" },
  "pending": { label: "Beklemede", color: "bg-slate-500" },
};

const FINANCIAL_STATUSES = {
  "paid": { label: "Ödendi", color: "bg-emerald-500" },
  "pending": { label: "Ödeme Bekliyor", color: "bg-amber-500" },
  "refunded": { label: "İade Edildi", color: "bg-red-500" },
  "partially_refunded": { label: "Kısmen İade", color: "bg-orange-500" },
  "voided": { label: "İptal", color: "bg-slate-500" },
};

export default function OrderSyncPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  
  // Orders
  const [shopifyOrders, setShopifyOrders] = useState([]);
  const [nebimOrders, setNebimOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("7d");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Sync Settings
  const [syncSettings, setSyncSettings] = useState({
    autoSync: false,
    syncInterval: 15,
    syncOnCreate: true,
    syncOnPayment: true,
    syncOnFulfillment: false,
    createCustomerInNebim: true,
    defaultWarehouse: "",
    defaultPosTerminal: ""
  });
  
  // Dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [syncLogDialogOpen, setSyncLogDialogOpen] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);

  useEffect(() => {
    fetchData();
  }, [token, statusFilter, dateFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/order-sync/orders?token=${token}&status=${statusFilter}&range=${dateFilter}`),
        axios.get(`${API}/order-sync/settings?token=${token}`)
      ]);
      
      setShopifyOrders(ordersRes.data.shopifyOrders || []);
      setNebimOrders(ordersRes.data.nebimOrders || []);
      if (settingsRes.data) {
        setSyncSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
    } catch (error) {
      console.error("Order sync fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncLogs = async () => {
    try {
      const response = await axios.get(`${API}/order-sync/logs?token=${token}&limit=50`);
      setSyncLogs(response.data.logs || []);
      setSyncLogDialogOpen(true);
    } catch (error) {
      toast.error("Loglar yüklenemedi");
    }
  };

  const syncSelectedOrders = async () => {
    if (selectedOrders.length === 0) {
      toast.error("Sipariş seçmelisiniz");
      return;
    }

    setSyncing(true);
    try {
      const response = await axios.post(`${API}/order-sync/sync?token=${token}`, {
        orderIds: selectedOrders
      });
      toast.success(`${response.data.synced || 0} sipariş Nebim'e aktarıldı`);
      setSelectedOrders([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Senkronizasyon başarısız");
    } finally {
      setSyncing(false);
    }
  };

  const syncAllOrders = async () => {
    if (!confirm("Tüm bekleyen siparişleri Nebim'e aktarmak istediğinizden emin misiniz?")) return;

    setSyncing(true);
    try {
      const response = await axios.post(`${API}/order-sync/sync-all?token=${token}`);
      toast.success(`${response.data.synced || 0} sipariş Nebim'e aktarıldı`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Senkronizasyon başarısız");
    } finally {
      setSyncing(false);
    }
  };

  const pullOrdersFromShopify = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/order-sync/pull-shopify?token=${token}`);
      toast.success(`${response.data.pulled || 0} yeni sipariş çekildi`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Siparişler çekilemedi");
    } finally {
      setSyncing(false);
    }
  };

  const saveSettings = async () => {
    try {
      await axios.put(`${API}/order-sync/settings?token=${token}`, syncSettings);
      toast.success("Ayarlar kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    }
  };

  const viewOrderDetail = (order) => {
    setSelectedOrder(order);
    setDetailDialogOpen(true);
  };

  const toggleSelectOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const toggleSelectAll = () => {
    const pendingOrders = shopifyOrders.filter(o => !o.syncedToNebim);
    if (selectedOrders.length === pendingOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(pendingOrders.map(o => o.id));
    }
  };

  const getStatusBadge = (status, type = "fulfillment") => {
    const statusMap = type === "fulfillment" ? ORDER_STATUSES : FINANCIAL_STATUSES;
    const statusInfo = statusMap[status] || { label: status, color: "bg-slate-500" };
    return <Badge className={statusInfo.color}>{statusInfo.label}</Badge>;
  };

  const filteredOrders = shopifyOrders.filter(order => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        order.orderNumber?.toLowerCase().includes(searchLower) ||
        order.customerName?.toLowerCase().includes(searchLower) ||
        order.email?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const pendingCount = shopifyOrders.filter(o => !o.syncedToNebim).length;
  const syncedCount = shopifyOrders.filter(o => o.syncedToNebim).length;

  return (
    <div className="space-y-6 fade-in" data-testid="order-sync-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Sipariş Senkronizasyonu</h1>
          <p className="text-slate-500 mt-1">Shopify siparişlerini Nebim'e aktarın</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={pullOrdersFromShopify} disabled={syncing}>
            <Download className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            Shopify'dan Çek
          </Button>
          <Button variant="outline" onClick={fetchSyncLogs}>
            <Clock className="w-4 h-4 mr-2" />
            Loglar
          </Button>
          <Button 
            onClick={syncSelectedOrders} 
            disabled={syncing || selectedOrders.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Upload className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            Seçilenleri Aktar ({selectedOrders.length})
          </Button>
        </div>
      </div>

      <Tabs defaultValue="orders" className="space-y-6">
        <TabsList>
          <TabsTrigger value="orders" className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" />
            Siparişler
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4" />
            Ayarlar
          </TabsTrigger>
        </TabsList>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ShoppingCart className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{shopifyOrders.length}</p>
                    <p className="text-xs text-slate-500">Toplam Sipariş</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{pendingCount}</p>
                    <p className="text-xs text-slate-500">Aktarım Bekleyen</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{syncedCount}</p>
                    <p className="text-xs text-slate-500">Aktarılan</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <CreditCard className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      ₺{shopifyOrders.reduce((sum, o) => sum + (o.totalPrice || 0), 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">Toplam Tutar</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Sipariş no, müşteri adı veya e-posta..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Durum" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Durumlar</SelectItem>
                    <SelectItem value="pending">Aktarım Bekleyen</SelectItem>
                    <SelectItem value="synced">Aktarılan</SelectItem>
                    <SelectItem value="failed">Hatalı</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24h">Son 24 Saat</SelectItem>
                    <SelectItem value="7d">Son 7 Gün</SelectItem>
                    <SelectItem value="30d">Son 30 Gün</SelectItem>
                    <SelectItem value="all">Tümü</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={syncAllOrders} disabled={syncing || pendingCount === 0}>
                  <Upload className="w-4 h-4 mr-2" />
                  Tümünü Aktar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Orders Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-blue-500" />
                Shopify Siparişleri
              </CardTitle>
              <CardDescription>{filteredOrders.length} sipariş listeleniyor</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Sipariş bulunamadı</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={selectedOrders.length === shopifyOrders.filter(o => !o.syncedToNebim).length && selectedOrders.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Sipariş No</TableHead>
                      <TableHead>Müşteri</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Ödeme</TableHead>
                      <TableHead className="text-right">Tutar</TableHead>
                      <TableHead className="text-center">Nebim</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((order) => (
                      <TableRow key={order.id} className={order.syncedToNebim ? "bg-emerald-50/50" : ""}>
                        <TableCell>
                          {!order.syncedToNebim && (
                            <Checkbox
                              checked={selectedOrders.includes(order.id)}
                              onCheckedChange={() => toggleSelectOrder(order.id)}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-mono font-medium">#{order.orderNumber}</span>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{order.customerName || "Misafir"}</p>
                            <p className="text-xs text-slate-500">{order.email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(order.createdAt).toLocaleDateString("tr-TR")}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(order.fulfillmentStatus, "fulfillment")}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(order.financialStatus, "financial")}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {order.currency} {order.totalPrice?.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-center">
                          {order.syncedToNebim ? (
                            <div className="flex items-center justify-center gap-1">
                              <CheckCircle className="w-5 h-5 text-emerald-500" />
                              <span className="text-xs text-slate-500">{order.nebimDocNo}</span>
                            </div>
                          ) : order.syncError ? (
                            <div className="flex items-center justify-center gap-1" title={order.syncError}>
                              <XCircle className="w-5 h-5 text-red-500" />
                            </div>
                          ) : (
                            <Clock className="w-5 h-5 text-slate-300 mx-auto" />
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="icon" variant="ghost" onClick={() => viewOrderDetail(order)}>
                            <Eye className="w-4 h-4 text-blue-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Otomatik Senkronizasyon</CardTitle>
              <CardDescription>Siparişlerin otomatik olarak Nebim'e aktarılma ayarları</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Otomatik Senkronizasyon</Label>
                  <p className="text-xs text-slate-500">Yeni siparişleri otomatik aktar</p>
                </div>
                <Switch
                  checked={syncSettings.autoSync}
                  onCheckedChange={(v) => setSyncSettings({...syncSettings, autoSync: v})}
                />
              </div>

              {syncSettings.autoSync && (
                <div className="space-y-2">
                  <Label>Senkronizasyon Aralığı</Label>
                  <Select 
                    value={syncSettings.syncInterval.toString()} 
                    onValueChange={(v) => setSyncSettings({...syncSettings, syncInterval: parseInt(v)})}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 dakika</SelectItem>
                      <SelectItem value="15">15 dakika</SelectItem>
                      <SelectItem value="30">30 dakika</SelectItem>
                      <SelectItem value="60">1 saat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="border-t pt-4 space-y-4">
                <p className="font-medium text-slate-700">Tetikleyiciler</p>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Sipariş Oluşturulduğunda</Label>
                      <p className="text-xs text-slate-500">Yeni sipariş geldiğinde aktar</p>
                    </div>
                    <Switch
                      checked={syncSettings.syncOnCreate}
                      onCheckedChange={(v) => setSyncSettings({...syncSettings, syncOnCreate: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Ödeme Alındığında</Label>
                      <p className="text-xs text-slate-500">Ödeme onaylandığında aktar</p>
                    </div>
                    <Switch
                      checked={syncSettings.syncOnPayment}
                      onCheckedChange={(v) => setSyncSettings({...syncSettings, syncOnPayment: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Kargolandığında</Label>
                      <p className="text-xs text-slate-500">Kargo bilgisi girildiğinde aktar</p>
                    </div>
                    <Switch
                      checked={syncSettings.syncOnFulfillment}
                      onCheckedChange={(v) => setSyncSettings({...syncSettings, syncOnFulfillment: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Müşteriyi Nebim'de Oluştur</Label>
                      <p className="text-xs text-slate-500">Yeni müşteri kaydı oluştur</p>
                    </div>
                    <Switch
                      checked={syncSettings.createCustomerInNebim}
                      onCheckedChange={(v) => setSyncSettings({...syncSettings, createCustomerInNebim: v})}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 space-y-4">
                <p className="font-medium text-slate-700">Varsayılan Değerler</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Varsayılan Depo</Label>
                    <Input
                      value={syncSettings.defaultWarehouse}
                      onChange={(e) => setSyncSettings({...syncSettings, defaultWarehouse: e.target.value})}
                      placeholder="Depo kodu"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Varsayılan POS Terminal</Label>
                    <Input
                      value={syncSettings.defaultPosTerminal}
                      onChange={(e) => setSyncSettings({...syncSettings, defaultPosTerminal: e.target.value})}
                      placeholder="Terminal kodu"
                    />
                  </div>
                </div>
              </div>

              <Button onClick={saveSettings}>
                Ayarları Kaydet
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Order Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Sipariş Detayı #{selectedOrder?.orderNumber}</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs text-slate-500">Müşteri</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">{selectedOrder.customerName || "Misafir"}</span>
                    </div>
                    <p className="text-sm text-slate-500">{selectedOrder.email}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Tarih</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span>{new Date(selectedOrder.createdAt).toLocaleString("tr-TR")}</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs text-slate-500">Teslimat Adresi</Label>
                    <div className="flex items-start gap-2 mt-1">
                      <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div className="text-sm">
                        <p>{selectedOrder.shippingAddress?.address1}</p>
                        <p>{selectedOrder.shippingAddress?.city}, {selectedOrder.shippingAddress?.country}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {getStatusBadge(selectedOrder.fulfillmentStatus, "fulfillment")}
                    {getStatusBadge(selectedOrder.financialStatus, "financial")}
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-xs text-slate-500">Ürünler</Label>
                <div className="mt-2 space-y-2">
                  {(selectedOrder.lineItems || []).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Package className="w-4 h-4 text-slate-400" />
                        <div>
                          <p className="font-medium text-sm">{item.title}</p>
                          <p className="text-xs text-slate-500">{item.sku} × {item.quantity}</p>
                        </div>
                      </div>
                      <span className="font-mono text-sm">{item.price} {selectedOrder.currency}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4 flex justify-between items-center">
                <span className="font-medium">Toplam</span>
                <span className="text-xl font-bold">{selectedOrder.totalPrice?.toFixed(2)} {selectedOrder.currency}</span>
              </div>

              {selectedOrder.syncedToNebim && (
                <Alert className="bg-emerald-50 border-emerald-200">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <AlertDescription className="text-emerald-700">
                    Bu sipariş Nebim'e aktarıldı. Doküman No: {selectedOrder.nebimDocNo}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          
          <DialogFooter>
            {!selectedOrder?.syncedToNebim && (
              <Button 
                onClick={() => {
                  setSelectedOrders([selectedOrder?.id]);
                  setDetailDialogOpen(false);
                  syncSelectedOrders();
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Nebim'e Aktar
              </Button>
            )}
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Logs Dialog */}
      <Dialog open={syncLogDialogOpen} onOpenChange={setSyncLogDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Senkronizasyon Logları</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {syncLogs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                Henüz log kaydı yok
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tarih</TableHead>
                    <TableHead>Sipariş</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead>Mesaj</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {syncLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm">
                        {new Date(log.created_at).toLocaleString("tr-TR")}
                      </TableCell>
                      <TableCell className="font-mono">#{log.orderNumber}</TableCell>
                      <TableCell>
                        {log.status === "success" ? (
                          <Badge className="bg-emerald-500">Başarılı</Badge>
                        ) : (
                          <Badge className="bg-red-500">Hatalı</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 max-w-[300px] truncate">
                        {log.message}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setSyncLogDialogOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
